"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Layers, Maximize2, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
interface AdditionType {
  id: string;
  title: string;
  description: string;
  scope: string;
  iconName: string;
}
const additionTypes: AdditionType[] = [{
  id: "second-story",
  title: "Second-story expansions",
  description: "Double your interior living space without sacrificing yard footprint or lot setbacks.",
  scope: "Structural reinforcement & full upper-level framing",
  iconName: "Layers"
}, {
  id: "ground-floor",
  title: "Ground-floor footprint bump-outs",
  description: "Expand kitchens, open family rooms, or add sunlit dining areas extending outward.",
  scope: "Foundation trenching, tie-ins & seamless roofline integration",
  iconName: "Maximize2"
}, {
  id: "primary-suite",
  title: "Dedicated primary suite wings",
  description: "Create a private retreat featuring custom walk-in closets, spa baths, and vaulted ceilings.",
  scope: "Architectural sound isolation & dedicated HVAC zoning",
  iconName: "Sparkles"
}];
const featuredTags = [{
  id: "1",
  label: "Second-Story Addition"
}, {
  id: "2",
  label: "Architectural Integration"
}, {
  id: "3",
  label: "Seamless Exterior Blend"
}];
export default function Additions() {
  const prefersReduced = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Layers":
        return <Layers className="size-5 text-chart-1" aria-hidden="true" />;
      case "Maximize2":
        return <Maximize2 className="size-5 text-chart-1" aria-hidden="true" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-chart-1" aria-hidden="true" />;
      default:
        return <CheckCircle2 className="size-5 text-chart-1" aria-hidden="true" />;
    }
  };
  const initialMotion = prefersReduced ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const animateMotion = {
    opacity: 1,
    y: 0
  };
  const transitionConfig = {
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="home-additions" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={initialMotion} whileInView={animateMotion} viewport={{
        once: true
      }} transition={transitionConfig} className="mb-12 sm:mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-chart-1 inline-block" aria-hidden="true" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground font-default">
              Architectural Expansions
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold font-serif tracking-tight text-foreground leading-tight">
            Home additions built for how you live
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Expand your square footage with second-story pop-tops, ground-floor extensions, and primary suite wings designed to blend into your Decatur home.
          </p>
        </motion.div>

        {/* 2-Column Asymmetrical Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Addition Cards & CTA */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {additionTypes.map((addition, i) => <motion.div key={addition.id} data-index={i} initial={initialMotion} whileInView={animateMotion} viewport={{
            once: true
          }} transition={{
            ...transitionConfig,
            delay: prefersReduced ? 0 : i * 0.08
          }} whileHover={prefersReduced ? {} : {
            y: -4
          }} className="transition-[border-color,box-shadow] duration-300">
                <Card className="bg-card/85 backdrop-blur-md border border-border shadow-xs hover:shadow-md hover:border-chart-1/40 transition-[border-color,box-shadow] duration-300 rounded-xl p-6 sm:p-7">
                  <CardHeader className="p-0 flex flex-row items-start gap-4">
                    <div className="size-11 rounded-lg bg-secondary flex items-center justify-center shrink-0 border border-border">
                      {getIcon(addition.iconName)}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg sm:text-xl font-serif font-semibold text-card-foreground">
                        {addition.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm sm:text-base mt-1.5 leading-relaxed">
                        {addition.description}
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 pt-4 mt-4 border-t border-border/60 flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                    <span className="font-semibold text-card-foreground">Scope focus:</span>
                    <span>{addition.scope}</span>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* Consultation CTA Block */}
            <motion.div initial={initialMotion} whileInView={animateMotion} viewport={{
            once: true
          }} transition={{
            ...transitionConfig,
            delay: prefersReduced ? 0 : 0.28
          }} className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 sm:p-6 rounded-xl bg-secondary/70 border border-border">
              <div>
                <p className="font-semibold text-foreground text-base">
                  Planning an expansion in Decatur?
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  We review local zoning, setback requirements, and architectural feasibility.
                </p>
              </div>
              <Button asChild size="lg" className="w-full sm:w-auto shrink-0 bg-chart-1 text-primary-foreground hover:bg-chart-1/90 transition-colors">
                <Link to="#" className="inline-flex items-center gap-2">
                  <span>Schedule consultation</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: Featured Project Showcase */}
          <motion.div initial={initialMotion} whileInView={animateMotion} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: prefersReduced ? 0 : 0.16
        }} className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-card">
              <AspectRatio ratio={4 / 5} className="bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_addition_exterior.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_addition_exterior.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_home_addition_exterior.webp-wvc-sizes" alt="Modern two-story architectural home addition in Decatur Georgia" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </AspectRatio>

              {/* Frosted Layered Overlay Card */}
              <div className="absolute inset-x-4 bottom-4 p-5 rounded-xl bg-card/90 backdrop-blur-md border border-border shadow-lg">
                <div className="flex flex-wrap gap-2 mb-3">
                  {featuredTags.map((tag, i) => <Badge key={tag.id} data-index={i} variant="secondary" className="text-xs font-medium py-1 px-2.5 rounded-md border border-border text-secondary-foreground">
                      {tag.label}
                    </Badge>)}
                </div>
                <h3 className="font-serif text-base sm:text-lg font-semibold text-card-foreground">
                  Custom Craftsman Integration
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                  Engineered to preserve original rooflines and neighborhood architectural guidelines.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}